import { apiDelete, apiGet, apiPost } from "../_apiShim";
import type { CookieApiResponse } from "@/types/responses/UserResponses";
import type { LoginResponse, TokenResponse } from "@/types/responses/UserResponses";

const BASE_PATH = "/api/v1/authentication/";

// =====================================================================
// EMAIL-FIRST REGISTRATION (verify → then collect)
// =====================================================================

// Step 1: request a magic link with email only
export async function requestRegistrationLink(
  email: string,
  languageCode: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}request-registration-link`,
    { email, languageCode },
    { "Cache-Control": "no-store" },
  );
}

// Resend the magic link (link-only template, 60s cooldown server-side)
export async function resendRegistrationLink(
  email: string,
  languageCode: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}resend-registration-link`,
    { email, languageCode },
    { "Cache-Control": "no-store" },
  );
}

// Step 2: read-only validate of the magic-link token (prefill the form).
// `invite`: the token's pending row carries an invite code (the person joins
// an organisation whose DPA already stands, no company is created), so the
// form asks for no DPA box and sends the terms+privacy map (W9 1-S, audit
// lens-7 F3). Optional: an older backend answers without it — the caller
// reads `?? false` and keeps the company lane.
export type RegistrationLinkInfo = {
  email: string;
  languageCode: string;
  invite?: boolean;
};
export async function validateRegistrationToken(
  token: string,
): Promise<RegistrationLinkInfo> {
  return await apiGet<RegistrationLinkInfo>(
    `${BASE_PATH}registration-link/${encodeURIComponent(token)}`,
    { "Cache-Control": "no-store" },
    true,
  );
}

// Step 3: submit the full form against the token → creates account + auto-login
export async function completeRegistration(
  token: string,
  firstName: string,
  lastName: string,
  password: string,
  country?: string,
  customerType?: string,
  taxId?: string,
  acceptedDocuments?: Record<string, string>,
): Promise<LoginResponse> {
  return await apiPost<LoginResponse>(
    `${BASE_PATH}complete-registration`,
    {
      token,
      firstName,
      lastName,
      password,
      country,
      customerType,
      taxId,
      acceptedDocuments,
    },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Request user registration (link-based activation)
export async function requestUserRegistration(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  languageCode: string,
  inviteCode?: string,
  country?: string,
  customerType?: string,
  taxId?: string,
  acceptedDocuments?: Record<string, string>,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}request-user-registration`,
    {
      firstName,
      lastName,
      email,
      password,
      languageCode,
      inviteCode,
      country,
      customerType,
      taxId,
      acceptedDocuments,
    },
    { "Cache-Control": "no-store" },
  );
}

// Activate account via email link token
export async function activateAccount(token: string): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}activate-account`,
    { token },
    { "Cache-Control": "no-store" },
  );
}

// Resend activation email for pending registration
export async function resendActivationEmail(
  email: string,
  languageCode: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}resend-activation-email`,
    { email, languageCode },
    { "Cache-Control": "no-store" },
  );
}

// Validate a password reset token
export async function validateResetToken(
  token: string,
): Promise<{ email: string; expiresAt: number }> {
  return await apiPost<{ email: string; expiresAt: number }>(
    `${BASE_PATH}validate-reset-token`,
    { token },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Reset password using a token from email link
export async function resetPasswordByToken(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}reset-password-by-token`,
    { token, newPassword },
    { "Cache-Control": "no-store" },
  );
}

// Register a user
export async function registerUser(
  providedOtp: string,
  firstName: string,
  lastName: string,
  password: string,
  languageCode: string,
  inviteCode?: string,
  country?: string,
  customerType?: string,
  taxId?: string,
): Promise<CookieApiResponse> {
  const response = await apiPost<CookieApiResponse>(
    `${BASE_PATH}register-user`,
    {
      providedOtp,
      firstName,
      lastName,
      password,
      languageCode,
      inviteCode,
      country,
      customerType,
      taxId,
    },
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response.companyId) throw new Error("unknown");

  return response;
}

// Login a user
export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  return await apiPost<LoginResponse>(
    `${BASE_PATH}login`,
    { email, password },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Logout a user
export async function logout(): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}logout`);
}

// Reissue an access token using the refresh token
export async function reissueAuthenticationToken(
  refreshToken?: string,
): Promise<TokenResponse> {
  return await apiPost<TokenResponse>(
    `${BASE_PATH}reissue-authentication-token`,
    refreshToken ? { refreshToken } : {},
    { "Cache-Control": "no-store" },
    true,
  );
}

// Request a password reset
export async function requestPasswordReset(email: string): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}request-password-reset`,
    { email },
    { "Cache-Control": "no-store" },
  );
}

// Reset password using OTP
export async function resetPasswordByOtp(
  otp: string,
  newPassword: string,
  email: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}reset-password-by-otp`,
    { otp, newPassword, email },
    { "Cache-Control": "no-store" },
  );
}

// Reset password using the old password
export async function resetPasswordByPassword(
  oldPassword: string,
  newPassword: string,
): Promise<TokenResponse> {
  return apiPost<TokenResponse>(
    `${BASE_PATH}reset-password-by-password`,
    { oldPassword, newPassword },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Request an email update
export async function requestEmailUpdate(
  newEmail: string,
  password: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}request-email-update`,
    { newEmail, password },
    { "Cache-Control": "no-store" },
  );
}

// Update email with OTP
export async function updateEmail(otp: string): Promise<TokenResponse> {
  return apiPost<TokenResponse>(
    `${BASE_PATH}update-email`,
    { otp },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Check OTP validity
export async function isValidOtp(otp: string, email: string): Promise<number> {
  const response = await apiGet<number>(
    `${BASE_PATH}valid-otp?otp=${encodeURIComponent(otp)}&email=${encodeURIComponent(email)}`,
    { "Cache-Control": "no-store" },
  );
  if (!response) throw new Error("invalidOtp");
  return response;
}

export async function resendOtp(
  email: string,
  firstName: string | null,
  languageCode: string,
): Promise<number> {
  const otpExpiration = await apiPost<number>(
    `${BASE_PATH}resend-otp`,
    { email, firstName, languageCode },
    { "Cache-Control": "no-store" },
  );
  if (!otpExpiration) throw new Error("authError");
  return otpExpiration;
}

// Verify MFA code
export async function verifyMfa(
  code: string,
  type: string,
): Promise<CookieApiResponse> {
  return await apiPost<CookieApiResponse>(
    "/api/v1/mfa/verify",
    { code, type },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Send MFA email code
export async function sendMfaEmailCode(): Promise<void> {
  await apiPost<void>(
    "/api/v1/mfa/send-email-code",
    {},
    { "Cache-Control": "no-store" },
  );
}

// Setup MFA (get secret and QR code URI)
export async function setupMfa(): Promise<{ secret: string; otpAuthUri: string }> {
  return await apiPost<{ secret: string; otpAuthUri: string }>(
    "/api/v1/mfa/setup",
    {},
    { "Cache-Control": "no-store" },
    true,
  );
}

// Confirm MFA setup with code
export async function confirmMfaSetup(code: string): Promise<{
  recoveryCodes: string[];
  accessToken?: string;
  refreshToken?: string;
  companyId?: string;
  userId?: string;
}> {
  return await apiPost<{
    recoveryCodes: string[];
    accessToken?: string;
    refreshToken?: string;
    companyId?: string;
    // Sent as a hex string by MfaResource on the forced-setup branch. It was
    // declared nowhere and therefore dropped here and in the handler, which
    // left this the only lane without a userId — no `veltric.userId`, and no
    // identity for the account stamp.
    userId?: string;
  }>(
    "/api/v1/mfa/confirm-setup",
    { code },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Disable MFA
export async function disableMfa(password: string, code: string): Promise<void> {
  await apiPost<void>(
    "/api/v1/mfa/disable",
    { password, code },
    { "Cache-Control": "no-store" },
  );
}

// Regenerate MFA recovery codes
export async function regenerateRecoveryCodes(
  password: string,
  code: string,
): Promise<{ recoveryCodes: string[] }> {
  return await apiPost<{ recoveryCodes: string[] }>(
    "/api/v1/mfa/regenerate-recovery-codes",
    { password, code },
    { "Cache-Control": "no-store" },
    true,
  );
}

// Resend OTP for registered user
export async function resendOtpRegisteredUser(
  type: string,
  companyId: string,
): Promise<number> {
  const response = await apiPost<number>(
    `${BASE_PATH}resend-otp-registered-user/${encodeURIComponent(type)}?companyId=${encodeURIComponent(companyId)}`,
    {},
    { "Cache-Control": "no-store" },
  );
  if (!response) throw new Error("authError");
  return response;
}
